/**
 * Saves conversation titles at most once each and never two at a time per conversation.
 *
 * The API asks for a title only while a conversation has none, and the answer that earns it
 * produces two title events — provisional, then final. The stream buffer replays both verbatim on
 * reconnect, sometimes through a second, lagging reader. Every title is a write to the same event
 * stream, so two things must hold: the final title is written last, and nothing written before it
 * is ever sent again once it has been claimed — an older title queued behind the newer one would
 * land last and stick (LAR-449).
 */
export type TitleWriteDeps = {
  update: (
    conversationId: string,
    title: string
  ) => Promise<{ success: boolean }>;
  /** A write that did not land. The title itself is not passed on: it is user content. */
  onFailure: (conversationId: string, error: unknown) => void;
};

type ConversationTitleWrites = {
  /** Every title already sent for this conversation — not only the last one. */
  sent: Set<string>;
  /** The most recently claimed title, the only one worth retrying after a failure. */
  latest: string;
  /** Writes run one after another. Always settles resolved, so nothing queued behind it is skipped. */
  queue: Promise<void>;
};

export type TitleWriteState = Map<string, ConversationTitleWrites>;

export const createTitleWriteState = (): TitleWriteState => new Map();

export const saveConversationTitle = (
  state: TitleWriteState,
  conversationId: string,
  title: string,
  deps: TitleWriteDeps
): Promise<void> => {
  let writes = state.get(conversationId);
  if (!writes) {
    writes = { sent: new Set(), latest: "", queue: Promise.resolve() };
    state.set(conversationId, writes);
  }
  const current = writes;

  if (current.sent.has(title)) return current.queue;
  current.sent.add(title);
  current.latest = title;

  const forget = (error: unknown) => {
    // Only the newest title is worth retrying. An older one that failed stays marked as sent, or
    // a replay would put it back on top of the newer one.
    if (current.latest === title) current.sent.delete(title);
    deps.onFailure(conversationId, error);
  };

  current.queue = current.queue
    .then(() => deps.update(conversationId, title))
    .then(
      (result) => {
        if (!result.success) forget(undefined);
      },
      (error: unknown) => forget(error)
    );
  return current.queue;
};
